import { useEffect, useMemo, useState } from "react";
import {
  estimateWeightTrend,
  evaluateNutritionTargetAdjustment,
  weeklyTrainingCounts,
  type ExercisePoint,
  type WeeklySummaryResult,
} from "@jilian/core";
import { useMeasurements, useSettings } from "../db/hooks";
import { saveNutritionTarget, saveMeasurement } from "../db/repo";
import { db } from "../db/db";
import { listExercisesWithHistory, loadExerciseSeries } from "../lib/trendsData";
import { loadWeeklySummaryData } from "../lib/weeklySummaryData";
import { localDateString } from "../lib/timezone";
import { uuid } from "../lib/ids";
import { Button, Card, RiskTag, Segmented, Stat } from "../components/ui";

type TrendsTab = "strength" | "weight" | "volume" | "weekly";

function LineChart({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const w = 320;
  const h = 120;
  const pad = 10;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = (w - pad * 2) / (points.length - 1);
  const coords = points.map((p, i) => ({
    x: pad + i * stepX,
    y: pad + (1 - (p - min) / range) * (h - pad * 2),
  }));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" aria-hidden="true" role="img">
      <polyline
        points={coords.map((c) => `${c.x},${c.y}`).join(" ")}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={3.5} fill="currentColor" />
      ))}
    </svg>
  );
}

export function Trends() {
  const settings = useSettings();
  const userId = settings?.profile?.id ?? "user-default";
  const measurements = useMeasurements(userId) ?? [];
  const nutrition = settings?.nutrition ?? null;
  const targetDays = settings?.targetTrainingDaysPerWeek ?? 4;

  const [activeTab, setActiveTab] = useState<TrendsTab>("strength");
  const [calDecline, setCalDecline] = useState(false);
  const [calRecovery, setCalRecovery] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [exercises, setExercises] = useState<Array<{ exerciseId: string; nameZh: string; lastDate: string }>>([]);
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const [exerciseSeries, setExerciseSeries] = useState<{ series: ExercisePoint[]; scope: string }>({ series: [], scope: "" });
  const [weekly, setWeekly] = useState<Array<{ weekLabel: string; count: number }>>([]);
  const [weightInput, setWeightInput] = useState("");
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummaryResult | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const today = localDateString();

  // 体重趋势（规则引擎：7 日均 + 30 日预测区间）
  const trend = useMemo(
    () =>
      estimateWeightTrend({
        measurements,
        averageDays: 7,
        projectionDays: 30,
        minimumDataPoints: 14,
      }),
    [measurements],
  );

  // 热量校准建议
  const calibration = useMemo(() => {
    if (!nutrition) return null;
    const daysSinceLastAdjustment = Math.max(
      0,
      Math.floor((Date.now() - new Date(nutrition.createdAt).getTime()) / 86_400_000),
    );
    return evaluateNutritionTargetAdjustment({
      measurements,
      currentTarget: nutrition,
      minimumWindowDays: 14,
      trainingTrendDeclining: calDecline,
      recoveryWorsening: calRecovery,
      minCaloriesStep: 100,
      maxCaloriesStep: 150,
      daysSinceLastAdjustment,
    });
  }, [measurements, nutrition, calDecline, calRecovery]);

  useEffect(() => {
    let mounted = true;
    const sessions = db.sessions;
    void (async () => {
      const [exList, allSessions] = await Promise.all([
        listExercisesWithHistory(),
        sessions.where("userId").equals(userId).toArray(),
      ]);
      if (!mounted) return;
      setExercises(exList);
      setSelectedExercise((prev) => prev ?? exList[0]?.exerciseId ?? null);
      setWeekly(weeklyTrainingCounts(allSessions, 4, today));
    })();
    return () => {
      mounted = false;
    };
  }, [today, measurements, userId]);

  useEffect(() => {
    if (!selectedExercise) {
      setExerciseSeries({ series: [], scope: "" });
      return;
    }
    let mounted = true;
    void loadExerciseSeries(selectedExercise).then((r) => {
      if (mounted) setExerciseSeries(r);
    });
    return () => {
      mounted = false;
    };
  }, [selectedExercise, measurements]);

  // 加载真实周报数据
  useEffect(() => {
    if (activeTab !== "weekly") return;
    let mounted = true;
    setLoadingSummary(true);
    void loadWeeklySummaryData({
      userId,
      referenceDate: today,
      targetTrainingDaysPerWeek: targetDays,
      dailyCalorieTarget: nutrition?.calories ?? 2200,
      dailyProteinTargetGrams: nutrition?.proteinGrams ?? 140,
    }).then((res) => {
      if (mounted) {
        setWeeklySummary(res);
        setLoadingSummary(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [activeTab, userId, today, targetDays, nutrition]);

  const acceptCalibration = async () => {
    if (!nutrition || !calibration || calibration.suggestedCalories == null) return;
    const next = {
      ...nutrition,
      id: uuid(),
      effectiveFrom: today,
      calories: calibration.suggestedCalories,
      reasonCodes: calibration.reasonCodes,
      calibrationWindowDays: calibration.windowDays,
      userAccepted: true,
      createdAt: new Date().toISOString(),
    };
    await saveNutritionTarget(next);
    setAccepted(true);
  };

  const handleSaveWeight = async () => {
    const val = parseFloat(weightInput);
    if (!Number.isFinite(val) || val <= 0) return;
    await saveMeasurement({
      id: uuid(),
      userId,
      date: today,
      weightKg: val,
      waistCm: null,
      bodyFatPercent: null,
      measurementContext: "morning_fasted",
      deviceId: "device-local-1",
      syncStatus: "local_only",
      createdAt: new Date().toISOString(),
    });
    setWeightInput("");
  };

  const series = exerciseSeries.series;
  const scopeLabel =
    exerciseSeries.scope === "per_dumbbell" ? "kg/只" : exerciseSeries.scope === "total" ? "kg 总重" : exerciseSeries.scope || "";

  return (
    <div className="space-y-4 pb-12">
      <header className="pt-2">
        <h1 className="text-[26px] font-bold text-tone-ink tracking-tight font-title">趋势分析</h1>
        <p className="text-[12px] font-medium text-tone-soft mt-0.5 font-caption">
          力量进阶 · 体重平滑 · 训练容量 · 周报汇总
        </p>
      </header>

      {/* 4-Tab 二级切换器 */}
      <Segmented<TrendsTab>
        value={activeTab}
        onChange={setActiveTab}
        options={[
          { value: "strength", label: "力量趋势" },
          { value: "weight", label: "体重趋势" },
          { value: "volume", label: "容量与热量" },
          { value: "weekly", label: "周报汇总" },
        ]}
      />

      {/* Tab 1: 力量趋势 */}
      {activeTab === "strength" && (
        <Card className="p-4 space-y-3.5 border border-tone-border">
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-bold text-tone-ink font-headline">⚡ 动作 e1RM 力量曲线</span>
            <RiskTag tone="accent">同动作同口径</RiskTag>
          </div>

          {exercises.length === 0 ? (
            <div className="rounded-elem border border-tone-border bg-black/[0.02] dark:bg-white/[0.03] p-4 text-center">
              <RiskTag tone="warn">暂无训练数据</RiskTag>
              <p className="text-[13px] text-tone-ink mt-1 font-body">完成首次训练并记录组数后，此处将自动绘制每个动作的 e1RM 进阶轨迹。</p>
            </div>
          ) : (
            <>
              {/* 横向动作滚筒选择 */}
              <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {exercises.slice(0, 12).map((e) => (
                  <button
                    key={e.exerciseId}
                    onClick={() => setSelectedExercise(e.exerciseId)}
                    className={`shrink-0 min-h-[38px] px-3.5 rounded-elem text-[12px] font-bold transition-all active:scale-95 ${
                      selectedExercise === e.exerciseId
                        ? "bg-tone-accent text-white shadow-sm"
                        : "bg-black/5 dark:bg-white/10 text-tone-ink border border-tone-border"
                    }`}
                  >
                    {e.nameZh}
                  </button>
                ))}
              </div>

              {series.length < 2 ? (
                <div className="rounded-elem border border-tone-border bg-black/[0.02] dark:bg-white/[0.03] p-4 text-center">
                  <RiskTag tone="warn">该动作数据积累中</RiskTag>
                  <p className="text-[13px] text-tone-ink mt-1 font-body">至少需要 2 次有效记录才会绘制趋势线，避免误导。</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[12px] font-medium text-tone-soft font-caption">重量（{scopeLabel}）</span>
                    <span className="text-[12px] font-bold text-tone-good font-caption">Epley 估算 1RM</span>
                  </div>
                  <div className="text-tone-accent py-2">
                    <LineChart points={series.map((p) => p.weightKg)} />
                  </div>
                  <div className="flex justify-between text-[11px] font-bold text-tone-soft font-caption">
                    <span>{series[0]?.date}</span>
                    <span>{series[series.length - 1]?.date}</span>
                  </div>

                  <div className="space-y-1.5 pt-2 border-t border-tone-border">
                    {series.map((p) => (
                      <div key={p.date} className="flex justify-between text-[12px] p-2 rounded-elem bg-black/[0.02] dark:bg-white/[0.03]">
                        <span className="text-tone-soft font-caption">{p.date}</span>
                        <span className="text-tone-ink font-semibold">
                          {p.weightKg} {scopeLabel} × {p.reps}次（余力 {p.rir}）· <strong className="text-tone-good font-bold font-display">e1RM ≈ {p.e1rm.toFixed(1)}</strong> {scopeLabel}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p className="text-[11px] text-tone-soft leading-relaxed font-caption">
                    * e1RM 仅在同一动作与同一重量口径内部比较；高次数组可信度较低，不鼓励为数值冒险进行真实 1RM 测试。
                  </p>
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* Tab 2: 体重趋势 */}
      {activeTab === "weight" && (
        <div className="space-y-3">
          <Card className="p-4 space-y-3.5 border border-tone-border">
            <div className="flex items-center justify-between">
              <span className="text-[15px] font-bold text-tone-ink font-headline">⚖️ 真实体重与 7 日平滑曲线</span>
              {trend.sevenDayAverageKg != null ? (
                <RiskTag tone="good">7 日均重 {trend.sevenDayAverageKg.toFixed(1)} kg</RiskTag>
              ) : null}
            </div>

            {measurements.length === 0 ? (
              <div className="rounded-elem border border-tone-border bg-black/[0.02] dark:bg-white/[0.03] p-4 text-center space-y-2">
                <RiskTag tone="accent">开启实时跟踪</RiskTag>
                <p className="text-sm text-tone-ink font-semibold font-body">暂无体重记录。录入今日体重后系统将自动绘制动态均线。</p>
              </div>
            ) : (
              <>
                <div className="text-tone-accent py-2">
                  <LineChart points={measurements.map((m) => m.weightKg)} />
                </div>
                <div className="flex justify-between text-[11px] font-bold text-tone-soft font-caption">
                  <span>最早：{measurements[0]?.date}</span>
                  <span>最新：{measurements[measurements.length - 1]?.date}</span>
                </div>
                {trend.available && trend.projectedChangeKg ? (
                  <div className="rounded-elem border border-tone-good/30 bg-tone-good/10 p-3">
                    <RiskTag tone="good">预计 30 天变化</RiskTag>
                    <p className="text-[13px] text-tone-ink mt-1 font-medium font-body">{trend.reasonText}</p>
                  </div>
                ) : (
                  <div className="rounded-elem border border-tone-border bg-black/[0.02] dark:bg-white/[0.03] p-3">
                    <RiskTag tone="warn">数据积累中</RiskTag>
                    <p className="text-[12px] text-tone-soft mt-1 leading-relaxed font-caption">{trend.reasonText}</p>
                  </div>
                )}
              </>
            )}

            {/* 快速录入 */}
            <div className="flex gap-2 pt-1 border-t border-tone-border">
              <input
                type="number"
                inputMode="decimal"
                value={weightInput}
                onChange={(e) => setWeightInput(e.target.value)}
                placeholder="录入今日体重 (kg)"
                className="flex-1 min-h-[44px] px-3.5 rounded-elem bg-black/5 dark:bg-white/10 text-sm font-semibold text-tone-ink placeholder:text-tone-soft/60"
              />
              <Button
                variant="secondary"
                className="min-h-[44px] px-4 font-bold text-xs rounded-elem"
                onClick={handleSaveWeight}
                disabled={!weightInput}
              >
                保存今日
              </Button>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-2">
            <Stat
              label="7 日均重"
              value={trend.sevenDayAverageKg != null ? `${trend.sevenDayAverageKg.toFixed(1)}` : "—"}
              unit="kg"
              sub="7 日平滑窗口"
            />
            <Stat
              label="预计 30 日"
              value={trend.projectedChangeKg != null ? `${trend.projectedChangeKg.low.toFixed(1)}~${trend.projectedChangeKg.high.toFixed(1)}` : "—"}
              unit="kg"
              sub="不足 14 天时不预测"
            />
          </div>
        </div>
      )}

      {/* Tab 3: 容量与热量 */}
      {activeTab === "volume" && (
        <div className="space-y-3">
          {/* 每周训练频率柱状图 */}
          <Card className="p-4 space-y-3 border border-tone-border">
            <div className="flex items-center justify-between">
              <span className="text-[15px] font-bold text-tone-ink font-headline">📊 每周训练出勤与容量</span>
              <RiskTag tone="good">近 4 周</RiskTag>
            </div>

            {weekly.every((w) => w.count === 0) ? (
              <div className="rounded-elem border border-tone-border bg-black/[0.02] dark:bg-white/[0.03] p-4 text-center">
                <RiskTag tone="warn">暂无完成训练</RiskTag>
                <p className="text-[13px] text-tone-ink mt-1 font-body">完成训练后这里会按训练归属日统计每周正式出勤。</p>
              </div>
            ) : (
              <>
                <div className="flex items-end justify-around gap-2 h-28 pt-2">
                  {weekly.map((w) => (
                    <div key={w.weekLabel} className="flex flex-col items-center gap-1.5 flex-1">
                      <span className="text-[12px] font-bold text-tone-ink font-display">{w.count} 次</span>
                      <div
                        className="w-full bg-tone-accent rounded-t-elem transition-all duration-500"
                        style={{ height: `${Math.max(6, (w.count / 5) * 80)}px` }}
                      />
                      <span className="text-[11px] font-medium text-tone-soft font-caption">{w.weekLabel}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-tone-soft pt-1 font-caption">
                  * 统计包含已完成（completed）及主动保护提前结算（ended_early）的训练场次。
                </p>
              </>
            )}
          </Card>

          {/* 热量校准建议 */}
          <Card className="p-4 space-y-3 border border-tone-border">
            <div className="flex items-center justify-between">
              <span className="text-[15px] font-bold text-tone-ink font-headline">🥗 热量校准建议</span>
              <span className="text-[11px] font-medium text-tone-soft font-caption">14–21 天窗口</span>
            </div>

            {!nutrition ? (
              <p className="text-[13px] text-tone-soft font-body">营养目标未初始化。</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  <RiskTag tone="accent">目标 {nutrition.calories} kcal</RiskTag>
                  <RiskTag tone="warn">蛋白 {nutrition.proteinGrams}g</RiskTag>
                  <RiskTag tone="warn">脂肪 {nutrition.fatGrams}g</RiskTag>
                  <RiskTag tone="warn">碳水 {nutrition.carbsGrams}g</RiskTag>
                </div>

                {calibration?.action === "insufficient_data" ? (
                  <div className="rounded-elem border border-tone-border bg-black/[0.02] dark:bg-white/[0.03] p-3">
                    <RiskTag tone="warn">数据积累中</RiskTag>
                    <p className="text-[13px] text-tone-ink mt-1 font-body">{calibration.reasonText}</p>
                  </div>
                ) : calibration?.action === "keep" ? (
                  <div className="rounded-elem border border-tone-good/30 bg-tone-good/10 p-3">
                    <RiskTag tone="good">当前维持良好</RiskTag>
                    <p className="text-[13px] text-tone-ink mt-1 font-body">{calibration.reasonText}</p>
                  </div>
                ) : calibration && calibration.suggestedCalories != null ? (
                  <div className="rounded-elem border border-tone-warn/40 bg-tone-warn/5 p-3 space-y-2">
                    <RiskTag tone="warn">建议校准热量</RiskTag>
                    <p className="text-[13px] text-tone-ink font-body">{calibration.reasonText}</p>
                    <Button className="w-full min-h-[44px] font-bold text-xs rounded-elem" disabled={accepted} onClick={() => void acceptCalibration()}>
                      {accepted ? "已应用新目标" : `采纳建议（调整为 ${calibration.suggestedCalories} kcal）`}
                    </Button>
                  </div>
                ) : null}

                <div className="border-t border-tone-border pt-2 space-y-2">
                  <div className="text-[12px] font-bold text-tone-ink font-caption">补充体感信号（影响校准方向）</div>
                  <label className="flex items-center gap-2 text-[13px] text-tone-ink min-h-[38px] font-body cursor-pointer">
                    <input type="checkbox" checked={calDecline} onChange={(e) => setCalDecline(e.target.checked)} className="w-4 h-4 rounded" />
                    多个动作表现持续下降
                  </label>
                  <label className="flex items-center gap-2 text-[13px] text-tone-ink min-h-[38px] font-body cursor-pointer">
                    <input type="checkbox" checked={calRecovery} onChange={(e) => setCalRecovery(e.target.checked)} className="w-4 h-4 rounded" />
                    深层恢复明显变差
                  </label>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Tab 4: 周报汇总（真实 DB 聚合） */}
      {activeTab === "weekly" && (
        <div className="space-y-3">
          {loadingSummary ? (
            <Card className="p-6 text-center text-tone-soft text-sm">
              正在生成近 7 天真实周报汇总…
            </Card>
          ) : !weeklySummary || weeklySummary.trainingDaysCount === 0 ? (
            <Card className="p-5 text-center space-y-2 border border-tone-border">
              <RiskTag tone="warn">本周暂无完成数据</RiskTag>
              <p className="text-sm text-tone-ink font-medium">
                本周暂无训练与饮食记录，完成训练并记餐后系统将自动汇总出勤、容量与改进建议。
              </p>
            </Card>
          ) : (
            <Card className="p-4 space-y-4 border border-tone-border shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[16px] font-bold text-tone-ink font-headline">
                    📅 本周训练与营养周报
                  </span>
                  <RiskTag tone="accent">
                    {weeklySummary.period.startDate} ~ {weeklySummary.period.endDate}
                  </RiskTag>
                </div>
                <RiskTag
                  tone={
                    weeklySummary.trainingAdherenceRate >= 1
                      ? "good"
                      : weeklySummary.trainingAdherenceRate >= 0.75
                        ? "accent"
                        : "warn"
                  }
                >
                  达成 {Math.round(weeklySummary.trainingAdherenceRate * 100)}%
                </RiskTag>
              </div>

              {/* 核心指标 4 格 */}
              <div className="grid grid-cols-2 gap-2">
                <Stat
                  label="出勤完成"
                  value={`${weeklySummary.trainingDaysCount}/${weeklySummary.targetTrainingDays}`}
                  unit="天"
                  sub={`达成率 ${Math.round(weeklySummary.trainingAdherenceRate * 100)}%`}
                />
                <Stat
                  label="总做功容量"
                  value={`${Math.round(weeklySummary.totalVolumeKg)}`}
                  unit="kg"
                  sub={`共 ${weeklySummary.totalCompletedSets} 组有效刺激`}
                />
                <Stat
                  label="日均热量"
                  value={`${Math.round(weeklySummary.averageCalories)}`}
                  unit="kcal"
                  sub={`差额 ${weeklySummary.calorieTargetDiff >= 0 ? "+" : ""}${Math.round(weeklySummary.calorieTargetDiff)} kcal`}
                />
                <Stat
                  label="日均蛋白质"
                  value={`${Math.round(weeklySummary.averageProteinGrams)}`}
                  unit="g"
                  sub={`目标 ${nutrition?.proteinGrams ?? 140}g / 日`}
                />
              </div>

              {/* 肌肉刺激分布 */}
              {weeklySummary.muscleVolumeBreakdown.length > 0 && (
                <div className="space-y-1.5 pt-1 border-t border-tone-border">
                  <div className="text-[12px] font-bold text-tone-ink">肌群覆盖情况：</div>
                  <div className="flex flex-wrap gap-1.5">
                    {weeklySummary.muscleVolumeBreakdown.map((m) => (
                      <span
                        key={m.muscle}
                        className="px-2 py-1 rounded bg-black/5 dark:bg-white/10 text-xs text-tone-ink font-medium"
                      >
                        {m.muscle} · {m.sets} 组
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 循证改进建议 */}
              {weeklySummary.actionableAdviceZh && (
                <div className="p-3 rounded-elem bg-tone-accentSoft/60 border border-tone-accent/20 space-y-1 text-xs text-tone-ink font-body">
                  <div className="font-bold flex items-center gap-1">
                    <span>💡 下周优化建议：</span>
                  </div>
                  <p className="opacity-90 leading-relaxed">
                    {weeklySummary.actionableAdviceZh}
                  </p>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}